import { APPCONFIG } from '../app.config';

const VERIFIER_KEY = 'sso_code_verifier';
const STATE_KEY = 'sso_state';
const ID_TOKEN_KEY = 'sso_id_token';

const base64url = (bytes) =>
    btoa(String.fromCharCode(...new Uint8Array(bytes)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const randomToken = (size) => base64url(crypto.getRandomValues(new Uint8Array(size)));

const challengeFrom = async (verifier) => {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return base64url(digest);
};

const fetchConfig = async () => {
    const response = await fetch(`${APPCONFIG.SITE.WEBAPI}api/sso/config`);
    if (!response.ok) throw new Error('No se pudo obtener la configuración de SSO');
    return response.json();
};

// forceLogin: Keycloak vuelve a pedir usuario y contraseña aunque tenga la sesión abierta.
export const startSsoLogin = async ({ forceLogin = false } = {}) => {
    const { issuer, clientId, redirectUri } = await fetchConfig();

    const verifier = randomToken(48);
    const state = randomToken(16);
    sessionStorage.setItem(VERIFIER_KEY, verifier);
    sessionStorage.setItem(STATE_KEY, state);
    const challenge = await challengeFrom(verifier);

    const params = new URLSearchParams({
        client_id: clientId,
        response_type: 'code',
        scope: 'openid profile email',
        redirect_uri: redirectUri,
        state,
        code_challenge: challenge,
        code_challenge_method: 'S256',
    });
    if (forceLogin) params.set('prompt', 'login');
    window.location.href = `${issuer}/protocol/openid-connect/auth?${params.toString()}`;
};

export const completeSsoLogin = async (code, state) => {
    const verifier = sessionStorage.getItem(VERIFIER_KEY);
    const expectedState = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);
    if (!verifier || !expectedState) throw new Error('No se encontró el verificador de la sesión');
    if (state !== expectedState) throw new Error('La respuesta de Keycloak no corresponde a este inicio de sesión');

    const response = await fetch(`${APPCONFIG.SITE.WEBAPI}api/sso/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, codeVerifier: verifier }),
    });
    if (!response.ok) {
        let message = 'No se pudo completar el ingreso';
        try { message = (await response.json()).message || message; } catch (e) {}
        throw new Error(message);
    }
    const data = await response.json();
    if (data.idToken) {
        try { localStorage.setItem(ID_TOKEN_KEY, data.idToken); } catch (e) {}
    }
    return data;
};

// La salida recarga la página (PrivateRoute manda a /logout por URL), así que la
// marca de "cerrando sesión" no puede vivir sólo en memoria: si se pierde,
// /login vuelve a entrar con la sesión de Keycloak que nadie cerró.
const LOGGING_OUT_KEY = 'sso_logging_out';
export const LOGOUT_DONE_PARAM = 'logout';

let loggingOut = false;
export const isLoggingOut = () => {
    try { return loggingOut || sessionStorage.getItem(LOGGING_OUT_KEY) === '1'; } catch (e) { return loggingOut; }
};
export const logoutFinished = () => {
    loggingOut = false;
    try { sessionStorage.removeItem(LOGGING_OUT_KEY); } catch (e) {}
};

// Si no se puede llegar a Keycloak, la sesión de allá sigue abierta: se avisa en
// /login en vez de volver a entrar solo con esa misma sesión.
export const ssoLogout = async () => {
    if (loggingOut) return;
    loggingOut = true;
    try { sessionStorage.setItem(LOGGING_OUT_KEY, '1'); } catch (e) {}
    let issuer, clientId;
    try {
        ({ issuer, clientId } = await fetchConfig());
    } catch (e) {
        window.location.href = `${window.location.origin}/login?logout_error=${encodeURIComponent(e.message || 'No se pudo cerrar la sesión')}`;
        return;
    }
    const params = new URLSearchParams({
        client_id: clientId,
        post_logout_redirect_uri: `${window.location.origin}/login?${LOGOUT_DONE_PARAM}=done`,
    });
    try {
        const idToken = localStorage.getItem(ID_TOKEN_KEY);
        if (idToken) params.set('id_token_hint', idToken);
        localStorage.removeItem(ID_TOKEN_KEY);
    } catch (e) {}
    window.location.href = `${issuer}/protocol/openid-connect/logout?${params.toString()}`;
};

export const ssoEnabled = () => APPCONFIG.AUTH.PROVIDER === 'keycloak';
